import { useState } from 'react'
import { formatDayTime, withCountRu } from '@dorixona/utils'
import { Icon } from '../../ui/Icon'
import { failureText } from '../../ui/Load'
import { useSession } from '../auth/session'
import { useEndSessions, useStaffSessions, type Staff } from './api'
import { describeAgent } from './model'

export interface StaffSessionsProps {
  person: Staff
}

/** Сколько сеансов видно сразу: у владельца их бывают десятки. */
export const SESSIONS_SHOWN = 10

/**
 * Где сотрудник сейчас вошёл: браузер, IP и последняя активность
 * (`GET /admin/staff/{id}/sessions`, новые сверху). «Завершить все» выходит
 * везде (`DELETE` по тому же адресу) — например, после потерянного телефона.
 * Свои сеансы тоже можно завершить: тогда выходим и здесь, иначе экран
 * работал бы до первого обновления токена и молча выкинул бы на вход.
 */
export function StaffSessions({ person }: StaffSessionsProps) {
  const { me, signOut } = useSession()
  const sessions = useStaffSessions(person.id, true)
  const end = useEndSessions()
  const self = person.id === me?.id
  const list = sessions.data ?? []
  const [all, setAll] = useState(false)
  const shown = all ? list : list.slice(0, SESSIONS_SHOWN)

  function endAll() {
    end.mutate(person.id, {
      onSuccess: () => {
        if (self) signOut()
      },
    })
  }

  return (
    <section className="ui-card" aria-labelledby="stf-sessions-h">
      <div className="ui-card-head">
        <h3 id="stf-sessions-h">Сеансы</h3>
        {sessions.data ? (
          <span
            className="ui-count"
            aria-label={withCountRu(list.length, ['сеанс', 'сеанса', 'сеансов'])}
          >
            {list.length}
          </span>
        ) : null}
        {list.length > 0 ? (
          <button
            type="button"
            className="ui-btn"
            data-size="sm"
            data-variant="secondary"
            disabled={end.isPending}
            aria-busy={end.isPending}
            onClick={endAll}
          >
            {self ? 'Выйти на всех устройствах' : 'Завершить все'}
          </button>
        ) : null}
      </div>

      <div className="ui-card-body stf-sessions-body">
        {sessions.isPending ? (
          <p className="stf-sessions-note" role="status">
            Загружаем сеансы…
          </p>
        ) : null}
        {sessions.isError ? (
          <p className="stf-sessions-note">{failureText(sessions.error)}</p>
        ) : null}
        {sessions.data && list.length === 0 ? (
          <p className="stf-sessions-note">Сейчас нигде не вошёл</p>
        ) : null}
        {end.isError ? (
          <div className="ui-error" role="alert">
            <Icon name="i-alert" />
            {failureText(end.error)}
          </div>
        ) : null}

        {list.length > 0 ? (
          <ul className="stf-sessions-list">
            {shown.map((session) => (
              <li key={session.id}>
                <span className="txt">
                  <span className="primary">{describeAgent(session.agent)}</span>
                  <span className="secondary">{session.ip || 'IP неизвестен'}</span>
                </span>
                <span className="secondary">
                  {session.lastUsed ? formatDayTime(session.lastUsed) : '—'}
                </span>
              </li>
            ))}
          </ul>
        ) : null}
        {list.length > SESSIONS_SHOWN ? (
          <button
            type="button"
            className="ui-btn"
            data-variant="ghost"
            data-size="sm"
            onClick={() => {
              setAll(!all)
            }}
          >
            {all ? 'Свернуть' : `Показать все (${list.length})`}
          </button>
        ) : null}
      </div>
    </section>
  )
}
